import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money, num, pct } from "@/lib/format";
import { Crown, Medal, Flame, Trophy, Zap, Loader2 } from "lucide-react";

const PERIODS = [["week", "This Week"], ["month", "This Month"], ["all", "All Time"]];
const RANK_COLOR = { 1: "#F59E0B", 2: "#9CA3AF", 3: "#B45309" };

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/motivation/leaderboard?period=${period}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  const row = (r) => (
    <tr key={r.rep_id} className="border-t border-border" data-testid={`leaderboard-row-${r.rank}`}>
      <td className="py-2 pr-3">
        {r.rank <= 3 ? (
          <Medal className="h-4 w-4" style={{ color: RANK_COLOR[r.rank] }} />
        ) : (
          <span className="text-sm text-muted-foreground">{r.rank}</span>
        )}
      </td>
      <td className="py-2 pr-4 font-medium">{r.rep}</td>
      <td className="py-2 pr-4 text-right tabular-nums">{num(r.quotes)}</td>
      <td className="py-2 pr-4 text-right tabular-nums text-[hsl(var(--success))]">{num(r.wins)}</td>
      <td className={`py-2 pr-4 text-right tabular-nums ${r.avg_margin_pct < 15 ? "text-[hsl(var(--destructive))]" : ""}`}>{pct(r.avg_margin_pct)}</td>
      <td className="py-2 pr-4 text-right tabular-nums">
        <span className={`inline-flex items-center gap-1 ${r.day_streak >= 3 ? "font-bold text-[hsl(var(--warning))]" : ""}`}>
          {r.day_streak >= 3 && <Flame className="h-3.5 w-3.5" />}{num(r.day_streak)}d
        </span>
      </td>
      <td className="py-2 text-right font-semibold tabular-nums">{money(r.total_value)}</td>
    </tr>
  );

  return (
    <div className="border border-border bg-card" data-testid="team-leaderboard">
      <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="label-caps flex items-center gap-1.5"><Trophy className="h-4 w-4 text-[hsl(var(--warning))]" /> Team Leaderboard</span>
        <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid="leaderboard-period">
          {PERIODS.map(([v, l]) => (
            <button
              key={v}
              data-testid={`leaderboard-period-${v}`}
              onClick={() => setPeriod(v)}
              className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${period === v ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-secondary"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className={`p-4 ${loading ? "opacity-60" : ""}`}>
        {!data ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : !data.rep_of_week ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No quoting activity in this window yet. Be the first on the board! 🚀</p>
        ) : (
          <>
            {/* Rep of the week */}
            <div className="mb-4 flex items-center justify-between rounded-lg border border-[hsl(var(--warning))]/40 bg-gradient-to-r from-[hsl(var(--warning))]/15 to-transparent p-4" data-testid="rep-of-week">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--warning))]/20">
                  <Crown className="h-6 w-6 text-[hsl(var(--warning))]" />
                </div>
                <div>
                  <div className="label-caps text-[hsl(var(--warning))]">{period === "week" ? "Rep of the Week" : period === "month" ? "Rep of the Month" : "Top Rep"}</div>
                  <div className="font-heading text-xl font-black tracking-tight" data-testid="rep-of-week-name">{data.rep_of_week.rep}</div>
                </div>
              </div>
              <div className="hidden gap-5 text-right sm:flex">
                <Stat label="Quotes" value={num(data.rep_of_week.quotes)} />
                <Stat label="Wins" value={num(data.rep_of_week.wins)} icon={Zap} />
                <Stat label="Avg Margin" value={pct(data.rep_of_week.avg_margin_pct)} />
                <Stat label="Streak" value={`${num(data.rep_of_week.day_streak)}d`} icon={Flame} />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="label-caps text-left text-muted-foreground">
                    <th className="pb-2 pr-3 font-semibold">#</th>
                    <th className="pb-2 pr-4 font-semibold">Rep</th>
                    <th className="pb-2 pr-4 text-right font-semibold">Quotes</th>
                    <th className="pb-2 pr-4 text-right font-semibold">Wins</th>
                    <th className="pb-2 pr-4 text-right font-semibold">Avg Margin</th>
                    <th className="pb-2 pr-4 text-right font-semibold">Streak</th>
                    <th className="pb-2 text-right font-semibold">Value</th>
                  </tr>
                </thead>
                <tbody>{data.leaderboard.map(row)}</tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">Streaks reflect consecutive quoting days · weekly board resets every 7 days.</p>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className="metric-num mt-0.5 flex items-center justify-end gap-1 text-lg font-bold">
        {Icon && <Icon className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />}{value}
      </div>
    </div>
  );
}
